import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical, Upload } from 'lucide-react';
import axios from 'axios';
import { useDeactivateTemplate, useTemplateList, useUpdateTemplate, useUploadTemplate } from './hooks';
import { AiTemplateComposer } from './AiTemplateComposer';
import { MergeFieldGuide } from './MergeFieldGuide';
import { useDocumentKinds } from './kindHooks';
import { defaultsEffectiveBound, humanizeDocumentKind } from './kindLabels';
import { TemplatePreview } from './TemplatePreview';
import { usePageTitle } from '../../hooks/usePageTitle';
import { RowGroup, RowField } from '../../components/RowField';
import { Sheet } from '../../components/Sheet';
import { Button } from '../../components/Button';
import { FilterChip } from '../../components/ListScreen';
import type { TemplateKind, TemplateListItem } from './types';

export function TemplatesPage() {
  usePageTitle('Templates');
  const { data: templates, isLoading } = useTemplateList(true);
  const { data: kinds } = useDocumentKinds();
  const upload = useUploadTemplate();

  const [form, setForm] = useState<{ file: File | null; name: string; kind: TemplateKind; isEffectiveDateBound: boolean; requiresSignature: boolean }>({
    file: null, name: '', kind: '', isEffectiveDateBound: false, requiresSignature: true
  });
  const effectiveKind = form.kind || kinds?.[0]?.name || '';
  const [error, setError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<string[] | null>(null);
  const [showDeactivated, setShowDeactivated] = useState(false);

  const visible = (templates ?? []).filter((t) => showDeactivated || t.isActive);

  const onFile = (f: File | null) => setForm((s) => ({ ...s, file: f, name: s.name || (f ? stripExt(f.name) : '') }));

  const onSubmit = async () => {
    if (!form.file || !form.name.trim() || !effectiveKind) return;
    setError(null);
    setInvalidFields(null);
    try {
      await upload.mutateAsync({ file: form.file, name: form.name.trim(), kind: effectiveKind, isEffectiveDateBound: form.isEffectiveDateBound, requiresSignature: form.requiresSignature });
      setForm({ file: null, name: '', kind: '', isEffectiveDateBound: false, requiresSignature: true });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { message?: string; invalidFieldNames?: string[] } | undefined;
        setError(data?.message ?? 'Upload failed.');
        setInvalidFields(data?.invalidFieldNames ?? null);
      } else setError('Upload failed.');
    }
  };

  const kindOptions = (kinds ?? []).map((k) => ({ value: k.name, label: humanizeDocumentKind(k.name) }));

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 pb-24 sm:pb-8">
      <div>
        <Link to="/documents" className="text-sm text-slate-500 hover:text-pine">← Documents</Link>
        <h1 className="mt-2 font-display text-2xl font-semibold text-ink">Document templates</h1>
        <p className="mt-1 text-sm text-slate-500">
          Upload <code>.docx</code> templates with <code>&#123;&#123;FieldName&#125;&#125;</code> merge tokens, or <code>.pdf</code> files for static sign-only documents.
        </p>
      </div>

      <AiTemplateComposer />
      <MergeFieldGuide />

      <section className="space-y-3">
        <h2 className="px-1 font-display text-sm font-semibold text-slate-500">Upload a template</h2>
        <label htmlFor="template-file" className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-surface px-6 py-8 text-center hover:border-pine hover:bg-slate-50">
          <Upload size={28} className="text-slate-400" />
          <div className="font-medium text-ink">{form.file ? form.file.name : 'Choose a .docx or .pdf'}</div>
          <input id="template-file" type="file" accept=".docx,.pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
        </label>

        <RowGroup>
          <RowField type="text" label="Name" value={form.name} placeholder="Template name" onCommit={(v) => setForm((s) => ({ ...s, name: v }))} />
          <RowField
            type="select"
            label="Kind"
            value={effectiveKind}
            options={kindOptions}
            onCommit={(v) => setForm((s) => ({ ...s, kind: v, isEffectiveDateBound: defaultsEffectiveBound(v) ? true : s.isEffectiveDateBound }))}
          />
          <RowField type="toggle" label="Expire after visit" value={form.isEffectiveDateBound} onChange={(v) => setForm((s) => ({ ...s, isEffectiveDateBound: v }))} />
          <RowField type="toggle" label="Requires signature" value={form.requiresSignature} onChange={(v) => setForm((s) => ({ ...s, requiresSignature: v }))} />
        </RowGroup>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <p>{error}</p>
            {invalidFields && invalidFields.length > 0 && <p className="mt-1 text-xs">Invalid field names: <code>{invalidFields.join(', ')}</code></p>}
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={onSubmit} disabled={!form.file || !form.name.trim() || upload.isPending}>
            {upload.isPending ? 'Uploading…' : 'Upload template'}
          </Button>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 px-1">
          <h2 className="font-display text-sm font-semibold text-slate-500">All templates</h2>
          <FilterChip active={showDeactivated} onClick={() => setShowDeactivated((v) => !v)}>Show deactivated</FilterChip>
        </div>

        {isLoading ? (
          <p className="px-1 text-sm text-slate-500">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="rounded-2xl bg-surface px-6 py-10 text-center text-sm text-slate-500 shadow-sm ring-1 ring-slate-200/70">
            {templates && templates.length > 0 ? 'No active templates. Toggle “Show deactivated”.' : 'No templates yet.'}
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((t) => (
              <TemplateCard key={t.id} template={t} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function TemplateCard({ template: t }: { template: TemplateListItem }) {
  const [menu, setMenu] = useState(false);
  const update = useUpdateTemplate();
  const deactivate = useDeactivateTemplate();

  return (
    <div className={`overflow-hidden rounded-2xl bg-surface shadow-sm ring-1 ring-slate-200/70 ${!t.isActive ? 'opacity-60' : ''}`}>
      <div className="aspect-[4/3] overflow-hidden border-b border-slate-100 bg-slate-50">
        <TemplatePreview templateId={t.id} />
      </div>
      <div className="flex items-start justify-between gap-2 p-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{t.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{humanizeDocumentKind(t.kind)}</span>
            {!t.requiresSignature && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">No signature</span>}
            {!t.isActive && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">Inactive</span>}
          </div>
        </div>
        {t.isActive && (
          <button type="button" onClick={() => setMenu(true)} aria-label="Actions" className="-mr-1 shrink-0 p-1 text-slate-400 hover:text-ink">
            <MoreVertical size={18} />
          </button>
        )}
      </div>

      {menu && (
        <Sheet open onClose={() => setMenu(false)} title={t.name}>
          <ul className="space-y-1">
            <li>
              <button
                type="button"
                disabled={update.isPending}
                onClick={() => { update.mutate({ id: t.id, name: t.name, kind: t.kind, isEffectiveDateBound: t.isEffectiveDateBound, requiresSignature: !t.requiresSignature, isActive: t.isActive }); setMenu(false); }}
                className="w-full rounded-lg px-3 py-3 text-left text-sm text-ink hover:bg-slate-50"
              >
                {t.requiresSignature ? 'Change to no signature' : 'Require a signature'}
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => { if (confirm(`Deactivate "${t.name}"?`)) deactivate.mutate(t.id); setMenu(false); }}
                className="w-full rounded-lg px-3 py-3 text-left text-sm text-danger hover:bg-slate-50"
              >
                Deactivate
              </button>
            </li>
          </ul>
        </Sheet>
      )}
    </div>
  );
}

function stripExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.substring(0, i) : name;
}
